/**
 * Client-local names for units, groups and cities: what this player calls
 * them on their own screen. Keyed by entity id (unit, group or city building
 * id), never sent to the sim or peers and kept per match in localStorage
 * (`namesKey`). An empty name means "no name": the default label ("Group 2",
 * "City 1") shows again. Pure (no React, no storage access).
 */
import { cleanName, MAX_NAME_LENGTH } from '../../lib/names';
import type { RuleSet } from '../rules';
import { isCity, type PlayerId, type SimState } from '../sim';
import { isExactSelection, ownGroups } from './groups';

/** Same cleaning and length cap as player names (lib/names.ts): `cleanName` is what a typed name goes through. */
export { cleanName, MAX_NAME_LENGTH };

export type Names = Readonly<Record<string, string>>;

/** No names: the same object every time, so "unchanged" compares by identity. */
export const NO_NAMES: Names = {};

/** `names` with `id` renamed to the cleaned `raw`; an empty result removes it. Returns `names` itself when nothing changes. */
export function withName(names: Names, id: string, raw: string): Names {
  const name = cleanName(raw);
  if ((names[id] ?? '') === name) return names;
  if (name === '') {
    const { [id]: _removed, ...rest } = names;
    return rest;
  }
  return { ...names, [id]: name };
}

/** The player's name for `id`, or undefined when it has none. */
export function nameOf(names: Names | undefined, id: string): string | undefined {
  const name = names?.[id];
  return name ? name : undefined;
}

/** `id`'s name, else the default label. */
export function labelOrDefault(names: Names | undefined, id: string, fallback: string): string {
  return nameOf(names, id) ?? fallback;
}

/** "Name · Warrior" for a named unit; the bare type name without a name. */
export function unitLabel(names: Names | undefined, id: string, typeName: string): string {
  const name = nameOf(names, id);
  return name ? `${name} · ${typeName}` : typeName;
}

/**
 * The name to draw over a unit on the map: the viewer's own units only (names
 * are client-local, so a rival's unit never has one). Undefined when unnamed.
 */
export function mapLabel(
  names: Names | undefined,
  unit: { id: string; owner: PlayerId },
  playerId: PlayerId
): string | undefined {
  return unit.owner === playerId ? nameOf(names, unit.id) : undefined;
}

/** The localStorage key for one player's names in one match. */
export function namesKey(seed: string, playerId: string): string {
  return `territile.names.${seed}.${playerId}`;
}

/** Names read back from storage (`raw` is the stored text or null); anything malformed is dropped. */
export function parseNames(raw: string | null): Names {
  if (raw === null) return NO_NAMES;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return NO_NAMES;
    const out: Record<string, string> = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (typeof value !== 'string') continue;
      const name = cleanName(value);
      if (name) out[id] = name;
    }
    return Object.keys(out).length > 0 ? out : NO_NAMES;
  } catch {
    return NO_NAMES;
  }
}

/** What F2 renames for the current selection, and where its input shows (`RenameSite`). */
export function renameTarget(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  selectedUnitIds: readonly string[],
  selectedBuildingId: string | null
): { id: string; site: 'dock' | 'chip' } | null {
  const own = selectedUnitIds.filter((id) => state.units[id]?.owner === playerId);
  if (selectedUnitIds.length > 0) {
    if (own.length === 1 && selectedUnitIds.length === 1) return { id: own[0], site: 'dock' };
    // Units selected but no lone unit or whole group: nothing to rename (not the building).
    const group = ownGroups(state, playerId).find((g) => isExactSelection(g, selectedUnitIds));
    return group ? { id: group.id, site: 'dock' } : null;
  }
  const city = selectedBuildingId ? state.buildings[selectedBuildingId] : undefined;
  if (city && city.owner === playerId && city.buildProgress >= 1 && isCity(city, rules)) {
    return { id: city.id, site: 'chip' };
  }
  return null;
}

/** The localStorage key of the list of stored name sets, newest last, so old matches' names can be dropped. */
export const NAMES_INDEX_KEY = 'territile.names.index';

/** Match name sets kept in storage; older ones are removed. */
export const MAX_KEPT_NAME_SETS = 5;

/** Sim ticks (0.1 s each) within which a match counts as just started, so names stored under its key are from an earlier match. */
export const FRESH_MATCH_TICKS = 10;

/** Whether a match at `tick` just began (a host reload mid-match keeps its names; a rematch in the same room starts clean). */
export function isFreshMatch(tick: number): boolean {
  return tick <= FRESH_MATCH_TICKS;
}

/**
 * The index after `key` was written at `now` (ms): `key` moved to the end,
 * only the newest MAX_KEPT_NAME_SETS kept, and `drop` the keys that fell off
 * (to remove from storage). `raw` is the stored index text or null.
 */
export function touchNamesIndex(
  raw: string | null,
  key: string,
  now: number
): { index: Array<[string, number]>; drop: string[] } {
  let old: Array<[string, number]> = [];
  try {
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    if (Array.isArray(parsed)) {
      old = parsed.filter(
        (e): e is [string, number] =>
          Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'number'
      );
    }
  } catch {
    // A damaged index starts over.
  }
  const all = [...old.filter(([k]) => k !== key), [key, now] as [string, number]].sort(
    (a, b) => a[1] - b[1]
  );
  const cut = Math.max(0, all.length - MAX_KEPT_NAME_SETS);
  return { index: all.slice(cut), drop: all.slice(0, cut).map(([k]) => k) };
}
